import { api } from "@/utils/apiClient";
const normalizeList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (payload && typeof payload === "object") {
    if (Array.isArray(payload.items)) return payload.items;
    if (Array.isArray(payload.data)) return payload.data;
  }
  return [];
};

const toId = (value) => String(value || "");

const extractYearNumber = (value) => {
  if (value == null) return null;
  if (typeof value === 'object') {
    const nested = value || {};
    const candidates = [nested.year, nested.value, nested.label];
    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const buildVehicleLabel = (vehicle = {}) => {
  const name =
    vehicle.variantName ||
    vehicle.label ||
    vehicle.display?.variantName ||
    vehicle.display?.modificationGroup ||
    vehicle.groupTitle ||
    vehicle.name ||
    "Variant";
  const bodyType =
    vehicle.bodyType ||
    vehicle.display?.bodyType ||
    "";
  return bodyType && String(bodyType).trim().toLowerCase() !== String(name).trim().toLowerCase()
    ? `${name} (${bodyType})`
    : name;
};

const normalizeModificationOption = (option = {}) => {
  const vehicleId =
    option.vehicleId ||
    option._id ||
    option.id ||
    option.variantId ||
    option.vehicle?.vehicleId ||
    option.vehicle?.id ||
    option.vehicle?._id ||
    "";

  return {
    ...option,
    vehicleId: toId(vehicleId),
    label: option.label || buildVehicleLabel(option),
    variantName:
      option.variantName || option.label || buildVehicleLabel(option),
    technicalGroup:
      option.technicalGroup ||
      option.display?.modificationGroup ||
      option.display?.generation ||
      option.groupTitle ||
      "",
    yearRangeLabel:
      option.yearRangeLabel ||
      (option.yearFrom && option.yearTo
        ? String(option.yearFrom) === String(option.yearTo)
          ? String(option.yearFrom)
          : `${option.yearFrom} - ${option.yearTo}`
        : ""),
  };
};

const unwrapModificationPayload = (payload) => {
  const visited = new Set();
  let current = payload;
  for (
    let i = 0;
    i < 4 && current && typeof current === "object" && !Array.isArray(current);
    i += 1
  ) {
    if (visited.has(current)) break;
    visited.add(current);
    if (
      Array.isArray(current.groups) ||
      Array.isArray(current.data) ||
      Array.isArray(current.items)
    ) {
      return current;
    }
    const next =
      current.data ||
      current.payload ||
      current.result ||
      current.response ||
      null;
    if (!next || next === current) break;
    current = next;
  }
  return current || {};
};

const parseModificationPayload = (payload) => {
  const parseJsonLike = (input, depth = 0) => {
    if (typeof input !== "string" || depth > 3) return input;

    const cleaned = input.replace(/^\uFEFF/, "").trim();
    const parseAttempt = (text) => {
      try {
        return JSON.parse(text);
      } catch {
        return null;
      }
    };

    const bare = cleaned.replace(/^[{]+|[}]+$/g, "");
    const candidates = [cleaned, `{${bare}}`];

    for (const candidate of candidates) {
      const parsed = parseAttempt(candidate);
      if (parsed === null) continue;
      if (typeof parsed === "string" && parsed !== input) {
        const nested = parseJsonLike(parsed, depth + 1);
        if (nested !== parsed) return nested;
      }
      return parsed;
    }

    const start = cleaned.search(/[\[{]/);
    const end = Math.max(cleaned.lastIndexOf("}"), cleaned.lastIndexOf("]"));
    if (start >= 0 && end > start) {
      const sliced = cleaned.slice(start, end + 1);
      const parsed = parseAttempt(sliced);
      if (parsed !== null) {
        if (typeof parsed === "string") {
          const nested = parseJsonLike(parsed, depth + 1);
          if (nested !== parsed) return nested;
        }
        return parsed;
      }
    }

    return input;
  };

  return parseJsonLike(payload);
};

const normalizeModificationGroups = (payload) => {
  const root = unwrapModificationPayload(parseModificationPayload(payload));
  const rawGroups = Array.isArray(root)
    ? root
    : Array.isArray(root?.groups)
      ? root.groups
      : Array.isArray(root?.data?.groups)
        ? root.data.groups
        : Array.isArray(root?.result?.groups)
          ? root.result.groups
          : Array.isArray(root?.payload?.groups)
            ? root.payload.groups
            : [];

  return rawGroups.map((group = {}) => {
    const options = Array.isArray(group.options) ? group.options : [];
    return {
      ...group,
      groupKey:
        group.groupKey ||
        String(group.groupTitle || "")
          .toLowerCase()
          .replace(/\s+/g, "-"),
      groupTitle: group.groupTitle || "Other",
      options: options.map(normalizeModificationOption),
    };
  });
};

const cache = {
  brands: null,
  modelsByBrand: new Map(),
  yearsByModel: new Map(),
  vehiclesByFilter: new Map(),
  modificationGroupsByFilter: new Map(),
};

export const invalidateVehicleCache = () => {
  cache.brands = null;
  cache.modelsByBrand.clear();
  cache.yearsByModel.clear();
  cache.vehiclesByFilter.clear();
  cache.modificationGroupsByFilter.clear();
};

export const getVehicleBrands = async () => {
  if (cache.brands) return cache.brands;
  const data = await api.get("/vehicle-brands?status=active&limit=200");
  const list = data?.items || (Array.isArray(data) ? data : []);
  cache.brands = Array.isArray(list) ? list : [];
  return cache.brands;
};

export const getModelsByBrand = async (brandId) => {
  if (!brandId) return [];
  if (cache.modelsByBrand.has(brandId)) return cache.modelsByBrand.get(brandId);
  const data = await api.get(
    `/vehicle-models?brandId=${brandId}&status=active&limit=200`,
  );
  const list = data?.items || (Array.isArray(data) ? data : []);
  const normalized = Array.isArray(list) ? list : [];
  cache.modelsByBrand.set(brandId, normalized);
  return normalized;
};

export const getModelYears = async (modelId, brandId = "") => {
  if (!modelId) return [];
  const key = `${brandId || ""}:${modelId}`;
  if (cache.yearsByModel.has(key)) {
    const cached = cache.yearsByModel.get(key);
    if (Array.isArray(cached) && cached.length > 0) return cached;
    cache.yearsByModel.delete(key);
  }

  const fetchYears = async (path) => {
    const data = await api.get(path);
    const list = normalizeList(data);
    return Array.isArray(list) ? list : [];
  };

  const params = new URLSearchParams({ modelId, status: "active" });
  if (brandId) params.set("brandId", brandId);
  
  let list = await fetchYears(`/vehicles/filters/years?${params.toString()}`);

  if (!list.length) {
    const fallbackParams = new URLSearchParams({ modelId, status: "active", limit: "200" });
    list = await fetchYears(`/vehicle-model-years?${fallbackParams.toString()}`);
  }

  if (!list.length) {
    const vehicleParams = new URLSearchParams({ modelId, status: "active", limit: "500" });
    if (brandId) vehicleParams.set("brandId", brandId);
    const vehiclesData = await api.get(`/vehicles?${vehicleParams.toString()}`);
    const vehicleList = normalizeList(vehiclesData);
    const years = new Set();
    list = vehicleList.reduce((acc, vehicle) => {
      const yearFrom = extractYearNumber(vehicle?.yearFrom) ?? extractYearNumber(vehicle?.year?.year) ?? extractYearNumber(vehicle?.yearId?.year);
      const yearTo = extractYearNumber(vehicle?.yearTo) ?? yearFrom;
      if (!Number.isFinite(yearFrom) && !Number.isFinite(yearTo)) return acc;
      const start = Number(yearFrom || yearTo);
      const end = Number(yearTo || yearFrom || start);
      for (let year = Math.min(start, end); year <= Math.max(start, end); year += 1) {
        if (years.has(year)) continue;
        years.add(year);
        acc.push({ _id: `derived-${year}`, year });
      }
      return acc;
    }, []);
  }

  const seen = new Set();
  const normalized = list.filter((item) => {
  const id = toId(item?._id || item?.id || item?.vehicleId || item?.year || item?.code || item?.slug);
  const yearValue = String(item?.year || item?.label || item?.name || "");
  const dedupeKey = id || yearValue;
  if (!dedupeKey) return false;
  if (seen.has(dedupeKey)) return false;
    seen.add(dedupeKey);
    return true;
  });

  if (normalized.length) {
    cache.yearsByModel.set(key, normalized);
  } else {
    cache.yearsByModel.delete(key);
  }
  return normalized;
};

export const getVariantsByYear = async (yearId) => {
  if (!yearId) return [];
  const data = await api.get(`/vehicle-variants?modelYearId=${yearId}`);
  const list = data?.items || (Array.isArray(data) ? data : []);
  return Array.isArray(list) ? list : [];
};

export const getVehiclesByFilter = async ({
  brandId,
  modelId,
  yearId,
} = {}) => {
  if (!brandId || !modelId || !yearId) return [];
  const key = `${brandId}:${modelId}:${yearId}`;
  if (cache.vehiclesByFilter.has(key)) return cache.vehiclesByFilter.get(key);
  const params = new URLSearchParams({
    brandId,
    modelId,
    yearId,
    limit: "200",
  });
  const data = await api.get(`/vehicles?${params.toString()}`);
  const list = data?.items || (Array.isArray(data) ? data : []);
  const normalized = Array.isArray(list) ? list : [];
  cache.vehiclesByFilter.set(key, normalized);
  return normalized;
};

export const getVehicleModificationGroups = async ({
  brandId,
  modelId,
  yearId,
} = {}) => {
  if (!brandId || !modelId || !yearId) return [];
  const key = `${brandId}:${modelId}:${yearId}`;
  if (cache.modificationGroupsByFilter.has(key))
    return cache.modificationGroupsByFilter.get(key);
  const params = new URLSearchParams({ brandId, modelId, yearId });
  if (typeof window !== "undefined") {
    console.error("[VEHICLE_DEBUG:modifications:request]", {
      brandId,
      modelId,
      yearId,
      url: `/vehicles/modifications?${params.toString()}`,
    });
  }
  const data = await api.get(`/vehicles/modifications?${params.toString()}`);
  console.log(data);
  const payload = parseModificationPayload(data?.data || data || {});
  const groups = normalizeModificationGroups(payload);
  if (typeof window !== "undefined") {
    console.error("[VEHICLE_DEBUG:modifications:response]", {
      rawType: typeof data,
      rawKeys: data && typeof data === "object" ? Object.keys(data) : [],
      payloadKeys:
        payload && typeof payload === "object" && !Array.isArray(payload)
          ? Object.keys(payload)
          : [],
      selectedYearId: payload?.selectedYearId || null,
      selectedYear: payload?.selectedYear || null,
      totalGroups: groups.length,
      firstGroup: groups[0] || null,
    });
  }
  if (groups.length > 0) {
    cache.modificationGroupsByFilter.set(key, groups);
  } else {
    cache.modificationGroupsByFilter.delete(key);
  }
  return groups;
};
